import { useEffect, useRef, useState } from 'react';

const getValue = <T>(key: string, defaultValue: T) => {
	const stored = localStorage.getItem(key);

	if (!stored) {
		return defaultValue;
	}

	const initial = JSON.parse(stored);

	return initial ?? defaultValue;
};

export const useLocalStorage = <T>(key: string, defaultValue: T): ReturnType<typeof useState<T>> => {
	const [value, setValue] = useState<T>(() => getValue(key, defaultValue));
	const prevKeyRef = useRef<string>();

	useEffect(() => {
		localStorage.setItem(key, JSON.stringify(value));
		window.dispatchEvent(
			new CustomEvent('local-storage-sync', {
				detail: { key, value },
			})
		);
	}, [key, value]);

	useEffect(() => {
		const handleStorageSync = (e: Event) => {
			const customEvent = e as CustomEvent;
			if (customEvent.detail && customEvent.detail.key === key) {
				const newValue = customEvent.detail.value;
				setValue((prev) => {
					if (prev === newValue) return prev;
					if (typeof prev === 'object' && typeof newValue === 'object' && JSON.stringify(prev) === JSON.stringify(newValue)) {
						return prev;
					}
					return newValue;
				});
			}
		};

		window.addEventListener('local-storage-sync', handleStorageSync);
		return () => {
			window.removeEventListener('local-storage-sync', handleStorageSync);
		};
	}, [key]);

	useEffect(() => {
		prevKeyRef.current = key;
	}, [key]);

	if (prevKeyRef.current !== undefined && prevKeyRef.current !== key) {
		prevKeyRef.current = key;
		setValue(getValue(key, defaultValue));
	}

	return [value, setValue] as ReturnType<typeof useState<T>>;
};
